import { getUserEmail } from "../getUserEmail";
import { normalizeMarkupSnapshot } from "./markupSettingsSnapshot";
import type { MarkupSettingsSnapshot } from "../../../models/markupSettings";

/**
 * Прежнее хранилище наценок в браузере (до CAB-778).
 *
 * Настройки лежали в localStorage по ключу с почтой пользователя, а выбор типов цен
 * по разделам — отдельным ключом. Теперь они живут на сервере, и этот модуль нужен
 * только для переезда: прочитать то, что человек настроил раньше, отдать на сервер
 * и стереть из браузера. Когда у всех активных пользователей запись уедет, модуль
 * можно удалить целиком.
 */

const LEGACY_MARGIN_KEY = "eplex.margin.settings.v1";
const MARKUP_BASE_KEY = "eplex.markup.settings.v1";
const SELECTED_BASE_KEY = "eplex.selected.price.types.v2";

function scopedKey(baseKey: string, email: string | null): string {
  return email ? `${baseKey}.${email}` : baseKey;
}

function readJson(key: string): unknown {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as unknown;
  } catch (error) {
    console.error("Markup local storage parse failed: ", error);
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Читает наценки, оставшиеся в браузере от прежней схемы. Ключ с почтой в приоритете:
 * ключ без почты — след загрузки до опознания пользователя. `null` — переносить нечего.
 */
export function readLocalMarkupSnapshot(): MarkupSettingsSnapshot | null {
  const email = getUserEmail();
  const candidates = email
    ? [scopedKey(MARKUP_BASE_KEY, email), MARKUP_BASE_KEY, LEGACY_MARGIN_KEY]
    : [MARKUP_BASE_KEY, LEGACY_MARGIN_KEY];

  const settingsKey = candidates.find((key) => isRecord(readJson(key)));
  if (!settingsKey) return null;

  const settings = readJson(settingsKey) as Record<string, unknown>;
  const selected = readJson(scopedKey(SELECTED_BASE_KEY, email)) ?? readJson(SELECTED_BASE_KEY);

  return normalizeMarkupSnapshot({ ...settings, selectedPriceTypeIds: selected });
}

/** Стирает все следы прежнего хранилища — и с почтой текущего пользователя, и без неё. */
export function clearLocalMarkupStorage(): void {
  const email = getUserEmail();
  const keys = [
    LEGACY_MARGIN_KEY,
    MARKUP_BASE_KEY,
    SELECTED_BASE_KEY,
    scopedKey(MARKUP_BASE_KEY, email),
    scopedKey(SELECTED_BASE_KEY, email),
  ];

  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Markup local storage cleanup failed: ", error);
    }
  }
}
